<script setup lang="ts">
interface RouteMetaCustom {
  mode?: 'out-in' | 'in-out' | 'default'
  disabled?: boolean
  transition?: string
  duration?: number
  layout?: 'BlankLayout' | 'HomeLayout'
}
import onscan from 'onscan.js/onscan.min.js'
import electronLog from 'electron-log'
import { ComponentInstance, onBeforeMount, ref, shallowRef } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import layouts from './layouts'
import { createGTMNoScriptSrc, createGTMScript } from '@lib/utils/gtmUtils'
import { enable as enableElectronCookies } from '@lib/packages/cookies'
import ScannerKeepAlive from '@components/ScannerKeepAlive.vue'
import InteractionKeepAlive from '@components/InteractionKeepAlive.vue'
import { disableGlobalUserInteraction, enableGlobalUserInteraction } from '@lib/utils/utils'
import { logError, logInfo, scopedLogger } from '@lib/utils/logUtils'

const log = scopedLogger('App.vue')
const logRouter = scopedLogger('@Router')

const router = useRouter()
const layout = shallowRef<ComponentInstance<unknown> | 'div'>('div')

const containerId = import.meta.env.RENDERER_VITE_CONTAINER_ID || 'XXX-YYYYYYYY'
const gtmContainerScriptContent = ref<string>(createGTMScript(`${containerId}`))
const gtmContainerNoScriptContent = ref<string>(createGTMNoScriptSrc(`${containerId}`))

router.beforeEach(() => {
  disableGlobalUserInteraction()
})

router.beforeResolve(() => {
  setTimeout(() => {
    enableGlobalUserInteraction()
  }, 250)
})

router.afterEach((to) => {
  const layoutName: string = to.meta?.['layout'] as string
  layout.value = layouts[layoutName] || 'div'
  logInfo(logRouter, 'Navigation To ' + to.fullPath)
})

// unhandled errors renderer
electronLog.errorHandler.startCatching({
  showDialog: false,
  onError({ error }) {
    logError(log, 'UNHANDLED_ERROR RENDERER ' + error.message)
  }
})

const getRouteMetaName = (meta: RouteMetaCustom) => {
  if (meta.disabled) return undefined
  return meta?.transition || 'fade'
}

const getRouteMetaMode = (meta: RouteMetaCustom) => {
  return meta?.mode || 'out-in'
}

const getRouteMetaDuration = (meta: RouteMetaCustom) => {
  return meta?.duration || 250
}

const setupAnalytics = () => {
  // enable electron cookies
  enableElectronCookies()

  // load gtm script
  const gtmScript = document.createElement('script')
  gtmScript.replaceChildren(gtmContainerScriptContent.value)
  document.head.appendChild(gtmScript)

  // load gtm noscript
  const gtmNoScript = document.createElement('noscript')
  const gtmNoScriptIframe = document.createElement('iframe')
  gtmNoScriptIframe.src = gtmContainerNoScriptContent.value
  gtmNoScript.replaceChildren(gtmNoScriptIframe)
  document.body.appendChild(gtmNoScript)
}

onBeforeMount(() => {
  setupAnalytics()
  /* init onScan */
  onscan.attachTo(document, {
    reactToPaste: true
  })
})
</script>

<template>
  <ScannerKeepAlive />
  <InteractionKeepAlive />
  <transition name="fade" mode="out-in">
    <component :is="layout || 'div'">
      <RouterView v-slot="{ Component: component, route }">
        <transition
          :name="getRouteMetaName(route.meta)"
          :mode="getRouteMetaMode(route.meta)"
          :duration="getRouteMetaDuration(route.meta)"
        >
          <component :is="component" :key="route.path" />
        </transition>
      </RouterView>
    </component>
  </transition>

  <DialogsWrapper />
</template>
